import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  effect,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import {
  Placement,
  autoUpdate,
  computePosition,
  flip,
  offset as fuiOffset,
  shift,
  size
} from '@floating-ui/dom';

import { AnchoredPopupStackService } from '@shared/services/anchored-popup/anchored-popup-stack.service';

import { resolvePopupPositioning } from './anchored-popup.utils';

/**
 * Presentational positioning shell for a popover anchored to an element. Owns
 * floating-ui positioning, Esc-to-close and outside-click-to-close (ignoring the anchor,
 * the popup itself, and any popup opened above it in the stack), and projects arbitrary
 * content.
 *
 * It also closes once its anchor is no longer in the document. Its state is a root singleton
 * rendered by an outlet that may outlive the anchor's view, and a navigation (Back, a shortcut)
 * fires no outside click: the popup would otherwise stay on the next page, positioned against
 * an empty box at the viewport's corner.
 */
@Component({
  selector: 'app-anchored-popup',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div #popup class="fixed top-0 left-0 z-100 flex flex-col" [style.width.px]="width()">
      <ng-content />
    </div>
  `,
  styles: ``
})
export class AnchoredPopupComponent implements AfterViewInit, OnDestroy {
  anchorEl = input.required<HTMLElement>();
  placement = input<Placement>('bottom-start');
  width = input<number>(300);
  offset = input<number>(8);
  /**
   * The popup state instance this shell renders — pass the outlet's own popup service so an
   * open triggered by a *different* popup's service doesn't suppress this one's dismissal.
   * Left unset, this shell falls back to suppressing on any in-progress open.
   */
  owner = input<unknown>();

  dismiss = output<void>();

  private readonly stack = inject(AnchoredPopupStackService);
  private readonly document = inject(DOCUMENT);
  private readonly popupRef = viewChild.required<ElementRef<HTMLElement>>('popup');
  private cleanupAutoUpdate?: () => void;
  private anchorWatch?: MutationObserver;

  constructor() {
    effect(() => {
      const popupEl = this.popupRef().nativeElement;
      const { reference, placement } = resolvePopupPositioning(
        this.anchorEl(),
        this.placement()
      );
      const offsetPx = this.offset();

      this.cleanupAutoUpdate?.();
      this.cleanupAutoUpdate = autoUpdate(reference, popupEl, () => {
        computePosition(reference, popupEl, {
          strategy: 'fixed',
          placement,
          middleware: [
            fuiOffset(offsetPx),
            flip(),
            shift({ padding: 8 }),
            size({
              padding: 8,
              apply: ({ availableHeight, elements }) => {
                elements.floating.style.maxHeight = `${availableHeight}px`;
              }
            })
          ]
        }).then(({ x, y }) => {
          Object.assign(popupEl.style, { left: `${x}px`, top: `${y}px` });
        });
      });
    });
  }

  ngAfterViewInit(): void {
    this.stack.push(this.popupRef().nativeElement);
    // Read once the mutations of a DOM change are all in: an anchor moved within the page is
    // removed and inserted again, and is only gone if it is still out by then.
    this.anchorWatch = new MutationObserver(() => this.dismissIfAnchorGone());
    this.anchorWatch.observe(this.document.body, { childList: true, subtree: true });
    // The outlet can be handed a state whose anchor went with a view torn down before it.
    queueMicrotask(() => this.dismissIfAnchorGone());
  }

  private dismissIfAnchorGone(): void {
    if (this.anchorWatch && !this.anchorEl().isConnected) this.dismiss.emit();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (!this.stack.isTopmost(this.popupRef().nativeElement)) return;
    this.dismiss.emit();
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (this.stack.isOpening(this.owner())) return;
    const target = event.target as Node;
    const popupEl = this.popupRef().nativeElement;
    if (popupEl.contains(target) || this.anchorEl().contains(target)) return;
    if (this.stack.above(popupEl).some((el) => el.contains(target))) return;
    this.dismiss.emit();
  }

  ngOnDestroy(): void {
    this.anchorWatch?.disconnect();
    this.anchorWatch = undefined;
    this.stack.remove(this.popupRef().nativeElement);
    this.cleanupAutoUpdate?.();
  }
}
